import { useTranslation } from 'react-i18next';
import type { RecordingDashboardSummary } from '@bliks-io/contracts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bliks-io/ui';
import { PROCESS_STATUS_FILL_CLASS } from '@/lib/process-utils';

interface RecordingProgressBarProps {
  summary: RecordingDashboardSummary;
}

export function RecordingProgressBar({ summary }: RecordingProgressBarProps) {
  const { t } = useTranslation();
  const { completedCount, inRecordingCount, recordingCompleteCount, notStartedCount, totalCount } =
    summary;

  if (totalCount === 0) return null;

  const segments = [
    {
      label: t('recordingDashboard.progress.notStarted'),
      count: notStartedCount,
      color: PROCESS_STATUS_FILL_CLASS.not_started
    },
    {
      label: t('recordingDashboard.progress.inRecording'),
      // `recordingCompleteCount` is a subset of `inRecordingCount`: the two
      // segments split that tab's processes between them.
      count: inRecordingCount - recordingCompleteCount,
      color: PROCESS_STATUS_FILL_CLASS.in_recording
    },
    {
      label: t('recordingDashboard.progress.recordingComplete'),
      count: recordingCompleteCount,
      color: PROCESS_STATUS_FILL_CLASS.recording_complete
    },
    {
      label: t('recordingDashboard.progress.completed'),
      count: completedCount,
      color: PROCESS_STATUS_FILL_CLASS.completed
    }
  ];

  // Largest-remainder rounding guarantees segments sum to exactly 100
  const raw = segments.map((seg) => (seg.count / totalCount) * 100);
  const pcts = raw.map(Math.floor);
  let remainder = 100 - pcts.reduce((a, b) => a + b, 0);
  const indices = raw.map((_, i) => i).sort((a, b) => (raw[b] % 1) - (raw[a] % 1));
  for (const i of indices) {
    if (remainder <= 0) break;
    pcts[i]++;
    remainder--;
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-muted-foreground">
        {t('recordingDashboard.progress.title', { count: totalCount })}
      </p>
      <TooltipProvider delayDuration={0}>
        <div className="flex h-10 w-full overflow-hidden rounded-md bg-muted">
          {segments.map(
            (seg, i) =>
              pcts[i] > 0 && (
                <Tooltip key={seg.label}>
                  <TooltipTrigger asChild>
                    <div
                      className={`${seg.color} flex cursor-default items-center justify-center overflow-hidden whitespace-nowrap px-2 text-xs font-medium text-white transition-all`}
                      style={{ width: `${pcts[i]}%` }}
                    >
                      {/* `min-w-0` so the flex item may shrink past its text and
                          the ellipsis applies; the tooltip carries the full label. */}
                      <span className="min-w-0 truncate">
                        {seg.label} {pcts[i]}%
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {seg.label}: {seg.count} ({pcts[i]}%)
                  </TooltipContent>
                </Tooltip>
              )
          )}
        </div>
      </TooltipProvider>
    </div>
  );
}
